import { getCookieConsentForCategory } from '@modules/cookie-utils';

import type { Analytics, AnalyticsUser, ExposureTrackEvent, PageEvent, TrackEvent } from './types';
import type { AnalyticsGroupEvent, AnalyticsIdentifyEvent } from '.cache/__types__';

export class ComposeAnalytics implements Analytics {
  name = 'compose';

  constructor(private destinations: Analytics[]) {
    this.initialize();
  }

  async initialize(): Promise<void> {
    if (!getCookieConsentForCategory('analytics')) {
      return;
    }

    await Promise.all(this.destinations.map((d) => d.initialize()));
  }

  addDestination(destination: Analytics): void {
    if (this.destinations.some((d) => d.name === destination.name)) {
      return; // Avoid adding the same destination multiple times
    }
    this.destinations = [...this.destinations, destination];
  }

  removeDestinationByName(name: string): void {
    this.destinations = this.destinations.filter((d) => d.name !== name);
  }

  private getConsentAwareDestinations(): Analytics[] {
    if (!getCookieConsentForCategory('analytics')) {
      return this.destinations.filter((analytics) => analytics.name !== 'console');
    }
    return this.destinations;
  }

  track<TEventName extends keyof AnalyticsEventMap>(e: {
    name: TEventName;
    properties: AnalyticsEventMap[TEventName];
  }): void;
  track(e: ExposureTrackEvent | Partial<TrackEvent>): void;
  track(e: any): void {
    this.getConsentAwareDestinations().map((d) => d.track(e));
  }
  page(e: Partial<PageEvent>): void {
    this.getConsentAwareDestinations().map((d) => d.page(e));
  }
  identify(e: Partial<AnalyticsIdentifyEvent>): void {
    this.getConsentAwareDestinations().map((d) => d.identify(e));
  }
  group(e: Partial<AnalyticsGroupEvent>): void {
    this.getConsentAwareDestinations().map((d) => d.group(e));
  }
  user(): AnalyticsUser | undefined {
    const users = this.getConsentAwareDestinations()
      .map((d) => d.user())
      .filter(Boolean);
    return users.length ? users[0] : undefined;
  }
  reset(): void {
    this.getConsentAwareDestinations().map((d) => d.reset());
  }
}
